import type { JSX, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRightOutlined,
  EnterOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import { authApi } from '../lib/authApi';
import { matchSections, navSearchItems } from '../lib/nav';
import { wikiApi, type GroupedSearchDto } from '../lib/wikiApi';

/**
 * Глобальный поиск ⌘K / Ctrl+K (HUB 2.0, план wk6 §1.1). Поле в шапке и
 * хоткей открывают оверлей; результаты сгруппированы: Статьи / Курсы /
 * Стандарты / Файлы / Разделы. «Разделы» — на клиенте из меню (lib/nav.ts,
 * только видимые вьюеру). Пусто — «Спросить УК →». ↑/↓ + Enter, Esc.
 *
 * Backend: GET /wiki/search/grouped (FTS russian + лог запроса).
 */
const OPEN_EVENT = 'wiki-open-search';

/** Открыть поиск программно (поле в шапке, пустые состояния страниц). */
export function openCmdK(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

type FlatItem =
  | { kind: 'article' | 'standard'; key: string; title: string; sub: string | null; slug: string; articleId: string }
  | { kind: 'file'; key: string; title: string; sub: string | null; articleSlug: string | null; fileKind: string }
  | { kind: 'course'; key: string; title: string; sub: string | null; id: string }
  | { kind: 'section'; key: string; title: string; sub: string | null; to: string };

const KIND_STYLE: Record<FlatItem['kind'], { color: string; soft: string }> = {
  article: { color: 'var(--fk-info)', soft: 'var(--fk-info-soft)' },
  course: { color: 'var(--fk-orange)', soft: 'var(--fk-orange-soft)' },
  standard: { color: 'var(--fk-success-text)', soft: 'var(--fk-success-soft)' },
  file: { color: 'var(--fk-chart-5)', soft: 'rgba(156, 91, 212, .12)' },
  section: { color: 'var(--fk-indigo)', soft: 'var(--fk-neutral-soft)' },
};

function iconOf(it: FlatItem): ReactNode {
  switch (it.kind) {
    case 'article':
      return <FileTextOutlined />;
    case 'standard':
      return <SafetyCertificateOutlined />;
    case 'course':
      return <ReadOutlined />;
    case 'section':
      return <ArrowRightOutlined />;
    case 'file':
      return it.fileKind === 'IMAGE' ? <FileImageOutlined /> : it.fileKind === 'PDF' ? <FilePdfOutlined /> : <FileOutlined />;
  }
}

function buildFlat(data: GroupedSearchDto | undefined, sections: Array<{ to: string; label: string }>): FlatItem[] {
  const flat: FlatItem[] = [];
  if (data) {
    // sub: сниппет совпадения по телу статьи приоритетнее excerpt — видно, ГДЕ нашлось.
    for (const a of data.articles) flat.push({ kind: 'article', key: `a:${a.id}`, title: a.title, sub: a.snippet ?? a.excerpt, slug: a.slug, articleId: a.id });
    for (const c of data.courses) flat.push({ kind: 'course', key: `c:${c.id}`, title: c.name, sub: c.description, id: c.id });
    for (const s of data.standards) flat.push({ kind: 'standard', key: `s:${s.id}`, title: s.title, sub: s.snippet ?? s.categoryName, slug: s.slug, articleId: s.id });
    for (const f of data.files) flat.push({ kind: 'file', key: `f:${f.id}`, title: f.fileName, sub: f.articleTitle, articleSlug: f.articleSlug, fileKind: f.kind });
  }
  for (const s of sections) flat.push({ kind: 'section', key: `n:${s.to}`, title: s.label, sub: 'Перейти в раздел', to: s.to });
  return flat;
}

const GROUP_LABEL: Record<FlatItem['kind'], string> = {
  article: 'Статьи',
  course: 'Курсы',
  standard: 'Стандарты',
  file: 'Файлы',
  section: 'Разделы',
};
const GROUP_ORDER: FlatItem['kind'][] = ['article', 'course', 'standard', 'file', 'section'];

export function CmdKSearch(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 }).data;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpen = (): void => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (open) {
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ('');
      setDebounced('');
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['wiki', 'grouped', debounced],
    queryFn: () => wikiApi.searchGrouped(debounced),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });

  const sections = useMemo(() => matchSections(navSearchItems(me), debounced), [me, debounced]);
  const flat = useMemo(() => buildFlat(debounced.length >= 2 ? data : undefined, sections), [data, sections, debounced]);
  const totalArticles = data?.totalArticles ?? 0;

  useEffect(() => {
    if (active >= flat.length) setActive(0);
  }, [flat.length, active]);

  // Держим активную строку в видимой области при навигации стрелками.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const close = (): void => setOpen(false);
  const go = (to: string): void => {
    close();
    navigate(to);
  };
  const goItem = (it: FlatItem): void => {
    switch (it.kind) {
      case 'article':
      case 'standard':
        if (it.articleId) wikiApi.searchClick(debounced, it.articleId);
        go(`/article/${encodeURIComponent(it.slug)}`);
        break;
      case 'course':
        go(`/course/${it.id}`);
        break;
      case 'file':
        go(it.articleSlug ? `/article/${encodeURIComponent(it.articleSlug)}` : '/disk');
        break;
      case 'section':
        go(it.to);
        break;
    }
  };
  const goAll = (): void => {
    if (debounced.length >= 2) go(`/search?q=${encodeURIComponent(debounced)}`);
  };

  const onInputKey = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flat[active]) goItem(flat[active]);
      else goAll();
    }
  };

  const groups = GROUP_ORDER.map((k) => ({ kind: k, items: flat.filter((f) => f.kind === k) })).filter((g) => g.items.length > 0);
  const searched = debounced.length >= 2 && !isFetching && !!data;
  const empty = searched && flat.length === 0;

  return (
    <div className="fk-hub-cmdk-overlay" onClick={close}>
      <div className="fk-hub-cmdk" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Поиск">
        <div className="fk-hub-cmdk-input">
          <SearchOutlined style={{ fontSize: 18, color: 'var(--fk-text-muted)' }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Что ищем? Статья, стандарт, курс, файл, раздел…"
            aria-label="Поисковый запрос"
          />
          {isFetching && <span className="fk-hub-muted" style={{ fontSize: 12 }}>…</span>}
          <kbd className="fk-hub-kbd">Esc</kbd>
        </div>

        <div className="fk-hub-cmdk-list" ref={listRef}>
          {debounced.length < 2 && (
            <div className="fk-hub-cmdk-empty">Введите минимум 2 символа. Ищем по заголовкам и тексту с учётом морфологии.</div>
          )}
          {empty && (
            <div className="fk-hub-cmdk-empty">
              Ничего не нашли по «{debounced}».{' '}
              <button type="button" className="fk-hub-linkbtn" style={{ fontSize: 14 }} onClick={() => go('/knowledge/ask')}>
                Спросить УК →
              </button>
            </div>
          )}
          {groups.map((g) => (
            <div key={g.kind}>
              <div className="fk-hub-cmdk-group">{GROUP_LABEL[g.kind]}</div>
              {g.items.map((it) => {
                const idx = flat.indexOf(it);
                const st = KIND_STYLE[it.kind];
                return (
                  <button
                    key={it.key}
                    type="button"
                    data-idx={idx}
                    className="fk-hub-cmdk-row"
                    data-active={idx === active ? 'true' : 'false'}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => goItem(it)}
                  >
                    <span className="fk-hub-pop-ic" style={{ background: st.soft, color: st.color }}>{iconOf(it)}</span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span className="fk-hub-ellipsis" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--fk-text)' }}>{it.title}</span>
                      {it.sub && <span className="fk-hub-ellipsis" style={{ display: 'block', fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 1 }}>{it.sub}</span>}
                    </span>
                    <EnterOutlined className="fk-hub-cmdk-enter" />
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="fk-hub-cmdk-foot">
          <ThunderboltOutlined style={{ color: 'var(--fk-orange)' }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            AI подсказывает по смыслу:{' '}
            {debounced.length >= 2 ? (
              <button type="button" className="fk-hub-linkbtn" onClick={() => go(`/ai?q=${encodeURIComponent(debounced)}`)}>
                спросить AI «{debounced.length > 40 ? `${debounced.slice(0, 40)}…` : debounced}»
              </button>
            ) : (
              '«как списать» найдёт «Акт списания в IIKO»'
            )}
          </span>
          {debounced.length >= 2 && totalArticles > 0 && (
            <button type="button" className="fk-hub-linkbtn" onClick={goAll}>
              Все статьи ({totalArticles}) →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
